'use client';

import Image from 'next/image';
import { useEffect, useMemo, useRef, useState, memo } from 'react';

interface RotatingNeuroMessageProps {
  messages?: Array<{
    Id: string;
    RuleName: string;
    Message: string;
    Scope: number;
    Gender: string;
    Icon: string;
  }>;
  settings?: {
    EnableRotation?: boolean;
    RotationTimeSeconds?: number;
  };
  loaded: boolean;
  largeSize?: boolean;
}

const RotatingNeuroMessage = memo(({ messages, settings, loaded, largeSize }: RotatingNeuroMessageProps) => {
  const [tick, setTick] = useState(0);
  const rotationStartRef = useRef(Date.now());

  const activeMessage = useMemo(() => {
    if (!settings?.EnableRotation || !messages?.length || !settings?.RotationTimeSeconds) {
      return null;
    }

    const elapsedSeconds = Math.floor((Date.now() - rotationStartRef.current) / 1000);
    const rotationIndex = Math.floor(elapsedSeconds / settings.RotationTimeSeconds);
    const safeIndex = rotationIndex % messages.length;

    return messages[safeIndex];
  }, [tick, settings?.EnableRotation, settings?.RotationTimeSeconds, messages]);

  useEffect(() => {
    if (!settings?.EnableRotation) return;

    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [settings?.EnableRotation]);

  if (!activeMessage || !loaded) return null;

  return (
    <div className="flex items-center py-2">
      <Image src={activeMessage.Icon as string} alt="icon" unoptimized={true} height={24} width={24} />
      <p className={`ml-1 text-base font-normal text-black sm:text-lg ${largeSize && 'sm:text-base sm:font-medium'}`}>{activeMessage.Message}</p>
    </div>
  );
});

export default RotatingNeuroMessage;
